import { Headline, type HeadlineProps } from '@lichtblick/photon/atoms/headline/headline';
import { Text, type TextProps } from '@lichtblick/photon/atoms/text/text';
import utilityStyles from '@lichtblick/photon/theme/utilities.module.scss';
import type { Colors } from '@lichtblick/photon/types/colors';
import classNames from 'classnames';
import type React from 'react';
import { type FC, forwardRef, type PropsWithChildren } from 'react';
import styles from './card.module.scss';

type CardWrapperProps = PropsWithChildren & React.HTMLAttributes<HTMLDivElement> & { topOffset?: boolean };

export const CardWrapper: FC<CardWrapperProps> = ({ children, className, topOffset, ...props }) => (
  <div className={classNames(styles.wrapper, topOffset && styles['wrapper-top-offset'], className)} {...props}>
    {children}
  </div>
);

type CardProps = PropsWithChildren & React.HTMLAttributes<HTMLDivElement>;

export const Card: FC<CardProps> = ({ children, ...props }) => (
  <div {...props} className={classNames(styles.card, props.className)}>
    {children}
  </div>
);

type CardBonusHeaderProps = PropsWithChildren & {
  background: Colors;
};

export const CardBonusHeader: FC<CardBonusHeaderProps> = ({ background, children }) => (
  <div className={classNames(styles['card-bonus-header'], utilityStyles[`theme-bg-${background}`])}>{children}</div>
);

type CardHeaderProps = PropsWithChildren & {
  color: Colors;
};

export const CardHeader: FC<CardHeaderProps> = ({ children, color }) => (
  <div className={classNames(styles['card-header'], utilityStyles[`theme-color-${color}`])}>{children}</div>
);

type CardPriceProps = PropsWithChildren & {
  background: Colors;
  color: Colors;
};

export const CardPrice: FC<CardPriceProps> = ({ background, children, color }) => (
  <div
    className={classNames(
      styles['card-price'],
      utilityStyles[`theme-color-${color}`],
      utilityStyles[`theme-bg-${background}`],
    )}
  >
    {children}
  </div>
);

export const CardContractDurationList: FC<PropsWithChildren> = ({ children }) => (
  <ul className={styles['card-contract-duration-list']}>{children}</ul>
);

export const CardContractDurationRow: FC<PropsWithChildren> = ({ children }) => (
  <li className={styles['card-contract-duration-row']}>{children}</li>
);

export const CardContractDurationLabel: FC<PropsWithChildren> = ({ children }) => (
  <div className={styles['card-contract-duration-label']}>{children}</div>
);

export const CardDurationTogglePlaceholder: FC<PropsWithChildren> = ({ children }) => (
  <div className={styles['card-duration-toggle-placeholder']}>{children}</div>
);

export const CardTextPlaceholder = forwardRef<HTMLElement, TextProps>((props, ref) => (
  <Text {...props} className={classNames(styles['card-text-placeholder'], props.className)} ref={ref} />
));

type CardHeadlinePlaceholder = HeadlineProps & PropsWithChildren;

export const CardHeadlinePlaceholder: FC<CardHeadlinePlaceholder> = (props) => (
  <Headline className={styles['card-headline-placeholder']} {...props} />
);

export const CardPriceHeadlinePlaceholder: FC<CardHeadlinePlaceholder> = (props) => (
  <Headline className={styles['card-price-headline-placeholder']} {...props} />
);

export const CardCtaPlaceholder = forwardRef<HTMLElement, TextProps>((props, ref) => (
  <Text {...props} className={classNames(styles['card-cta-placeholder'], props.className)} ref={ref} />
));

export const CardLinkPlaceholder = forwardRef<HTMLElement, TextProps>((props, ref) => (
  <Text {...props} className={classNames(styles['card-link-placeholder'], props.className)} ref={ref} />
));
